export function getpolColours(){
  const DISTINCT = ["#4e79a7","#f28e2b","#e15759","#76b7b2","#59a14f","#edc948","#b07aa1","#ff9da7","#9c755f","#bab0ab"];
  return DISTINCT;
}

export function getPolConfig() {
  const groupsById = {

    // =========================================================================
    // ECONOMIC OUTLOOK
    // =========================================================================

    econ_approve: {
      type: "likert",
      label: "Economic approval",
      subgroup: "Economic Outlook",
      core: true,
      fulltext: "Overall, do you approve or disapprove of the administration's handling of the economy?",
      // Wave 4 added a "don't know" option to this question. Declaring only the
      // original three keys made wave 4 sum to 96.2 instead of 100, which is
      // why this group used to stop at wave 3: the missing 3.8 would have shown
      // up as a short stacked bar and an overstated disapprove share.
      //
      // The column does not exist in waves 1-3, and that is fine: the parquet
      // read unions by name, so those waves return null for it and aggregate to
      // 0.0. Verified through the loader at CD level, all four waves now sum to
      // 100 (dk: 0.0 / 0.0 / 0.0 / 3.8).
      //
      // dk sits in `neu` so net stays approve minus disapprove and nothing that
      // reads this group's polarity changes.
      keys: [
        "pol_vote:econapprove_approve:lik",
        "pol_vote:econapprove_neither:lik",
        "pol_vote:econapprove_disapprove:lik",
        "pol_vote:econapprove_dk:lik",
      ],
      labels: {
        "pol_vote:econapprove_approve:lik":    "Approve",
        "pol_vote:econapprove_neither:lik":    "Neither",
        "pol_vote:econapprove_disapprove:lik": "Disapprove",
        "pol_vote:econapprove_dk:lik":         "Don't know",
      },
      colors: {
        "pol_vote:econapprove_approve:lik":    "#2E7D32",
        "pol_vote:econapprove_neither:lik":    "#bab0ab",
        "pol_vote:econapprove_disapprove:lik": "#C62828",
        "pol_vote:econapprove_dk:lik":         "#d3d3d3",
      },
      polarity: {
        pos: "pol_vote:econapprove_approve:lik",
        neu: ["pol_vote:econapprove_neither:lik", "pol_vote:econapprove_dk:lik"],
        neg: "pol_vote:econapprove_disapprove:lik",
      },
      schema: "tracking",
      waves: [1, 2, 3, 4],
    },

    michigan_expectations: {
      type: "likert",
      label: "Price expectations (12 months)",
      subgroup: "Economic Outlook",
      core: true,
      fulltext: "During the next 12 months, do you think that prices in general will go up, or go down, or stay where they are now?",
      keys: [
        "michigan_prices_expectations__up_pred",
        "michigan_prices_expectations__same_pred",
        "michigan_prices_expectations__down_pred",
        "michigan_prices_expectations__dk_pred",
      ],
      labels: {
        "michigan_prices_expectations__up_pred":   "Up",
        "michigan_prices_expectations__same_pred": "Same",
        "michigan_prices_expectations__down_pred": "Down",
        "michigan_prices_expectations__dk_pred":   "Don't know",
      },
      polarity: {
        neg: "michigan_prices_expectations__up_pred",
        neu: "michigan_prices_expectations__same_pred",
        pos: "michigan_prices_expectations__down_pred",
        dk:  "michigan_prices_expectations__dk_pred",
      },
      schema: "tracking",
      // Wave 4 dropped this question: the michigan_prices_expectations__*
      // columns are not in the wave 4 parquet at all, so declaring 4 offered a
      // wave selector that returned nothing.
      waves: [2, 3],
    },

    pol_tariffs_effect: {
      type: "likert",
      label: "Impact of tariffs",
      subgroup: "Economic Outlook",
      core: true,
      fulltext: "Overall, do you think the tariffs placed on imported goods help or hurt the economy?",
      keys: [
        "tariffs_help_hurt__help_pred",
        "tariffs_help_hurt__hurt_pred",
        "tariffs_help_hurt__dk_pred",
      ],
      labels: {
        "tariffs_help_hurt__help_pred": "Help",
        "tariffs_help_hurt__hurt_pred": "Hurt",
        "tariffs_help_hurt__dk_pred":   "Don't know",
      },
      polarity: {
        pos: "tariffs_help_hurt__help_pred",
        neg: "tariffs_help_hurt__hurt_pred",
        neu: "tariffs_help_hurt__dk_pred",
      },
      schema: "tracking",
      // Wave 4 carries all three tariffs_help_hurt__* columns and sums to 100.
      waves: [2, 3, 4],
    },

    // =========================================================================
    // POLITICAL
    // =========================================================================

    pol_top_issues: {
      type: "categorical",
      label: "Top Issues",
      subgroup: "Political",
      core: true,
      fulltext: "Which of the following issues do you think is most important facing the country today?",
      keys: [
        "top_issues__topissue_abortion_pred",
        "top_issues__topissue_ai_pred",
        "top_issues__topissue_climate_pred",
        "top_issues__topissue_crime_pred",
        "top_issues__topissue_drugadd_pred",
        "top_issues__topissue_economyjobs_pred",
        "top_issues__topissue_education_pred",
        "top_issues__topissue_govspending_pred",
        "top_issues__topissue_gunviolence_pred",
        "top_issues__topissue_healthcare_pred",
        "top_issues__topissue_housing_pred",
        "top_issues__topissue_immigration_pred",
        "top_issues__topissue_inflation_pred",
        "top_issues__topissue_natsec_pred",
        "top_issues__topissue_poldivision_pred",
        "top_issues__topissue_racerelations_pred",
        "top_issues__topissue_socialmedia_pred",
        "top_issues__topissue_taxes_pred",
      ],
      colors: (() => {
        const palette = getpolColours();
        const keys = [
          "top_issues__topissue_abortion_pred",
          "top_issues__topissue_ai_pred",
          "top_issues__topissue_climate_pred",
          "top_issues__topissue_crime_pred",
          "top_issues__topissue_drugadd_pred",
          "top_issues__topissue_economyjobs_pred",
          "top_issues__topissue_education_pred",
          "top_issues__topissue_govspending_pred",
          "top_issues__topissue_gunviolence_pred",
          "top_issues__topissue_healthcare_pred",
          "top_issues__topissue_housing_pred",
          "top_issues__topissue_immigration_pred",
          "top_issues__topissue_inflation_pred",
          "top_issues__topissue_natsec_pred",
          "top_issues__topissue_poldivision_pred",
          "top_issues__topissue_racerelations_pred",
          "top_issues__topissue_socialmedia_pred",
          "top_issues__topissue_taxes_pred",
        ];
        return Object.fromEntries(keys.map((k, i) => [k, palette[i % palette.length]]));
      })(),
      labels: {
        top_issues__topissue_abortion_pred:     "Abortion",
        top_issues__topissue_ai_pred:           "Artificial intelligence",
        top_issues__topissue_climate_pred:      "Climate change",
        top_issues__topissue_crime_pred:        "Crime",
        top_issues__topissue_drugadd_pred:      "Drug addiction",
        top_issues__topissue_economyjobs_pred:  "Economy and jobs",
        top_issues__topissue_education_pred:    "Education",
        top_issues__topissue_govspending_pred:  "Government spending",
        top_issues__topissue_gunviolence_pred:  "Gun violence",
        top_issues__topissue_healthcare_pred:   "Healthcare",
        top_issues__topissue_housing_pred:      "Housing",
        top_issues__topissue_immigration_pred:  "Immigration",
        top_issues__topissue_inflation_pred:    "Inflation",
        top_issues__topissue_natsec_pred:       "National security",
        top_issues__topissue_poldivision_pred:  "Political division",
        top_issues__topissue_racerelations_pred:"Race relations",
        top_issues__topissue_socialmedia_pred:  "Social media",
        top_issues__topissue_taxes_pred:        "Taxes",
      },
      schema: "tracking",
      waves: [2, 3, 4],
    },

    pol_midterm_vote_choice: {
      type: "categorical",
      label: "Midterm voting intention",
      subgroup: "Political",
      core: true,
      fulltext: "Thinking ahead to the Midterm elections next November - who do you think you will vote for?",
      keys: [
        "midterms_VI_dem",
        "midterms_VI_rep",
        "midterms_VI_other",
        "midterms_VI_notsure_wnv",
      ],
      labels: {
        "midterms_VI_dem":          "Democrat",
        "midterms_VI_rep":          "Republican",
        "midterms_VI_other":        "Other",
        "midterms_VI_notsure_wnv":  "Not sure / Would not vote",
      },
      colors: {
        "midterms_VI_dem":         "#0057B7",
        "midterms_VI_rep":         "#D52B1E",
        "midterms_VI_other":       "#CCCCCC",
        "midterms_VI_notsure_wnv": "#888888",
      },
      aggregation_column: "TURNOUT_N",
      hidden_in_demo: true,
      schema: "tracking",
      waves: [2, 3],
    },

    immig_attitudes: {
      type: "likert",
      label: "Immigration policy approval",
      subgroup: "Political",
      core: true,
      fulltext: "How much do you agree or disagree with the current administration's approach to immigration?",
      // Wave 4 added a "don't know" option, exactly as econ_approve did. The
      // column does not exist in waves 1-3, which is harmless: the parquet read
      // unions by name so those waves return null and aggregate to 0.0.
      // Declaring wave 4 WITHOUT this key would have summed to 96.6.
      keys: [
        "pol_policy:immig_agree:lik",
        "pol_policy:immig_neither:lik",
        "pol_policy:immig_disagree:lik",
        "pol_policy:immig_dk:lik",
      ],
      labels: {
        "pol_policy:immig_agree:lik":    "Agree",
        "pol_policy:immig_neither:lik":  "Neither",
        "pol_policy:immig_disagree:lik": "Disagree",
        "pol_policy:immig_dk:lik":       "Don't know",
      },
      // dk sits in `neu` so net stays agree minus disagree.
      polarity: {
        pos: "pol_policy:immig_agree:lik",
        neu: ["pol_policy:immig_neither:lik", "pol_policy:immig_dk:lik"],
        neg: "pol_policy:immig_disagree:lik",
      },
      schema: "tracking",
      waves: [1, 2, 3, 4],
    },

    // =========================================================================
    // PERSONAL FINANCES
    // =========================================================================

    pol_econ_direction: {
      type: "likert",
      label: "Personal finances direction",
      subgroup: "Personal Finances",
      core: true,
      fulltext: "Would you say that you are better off or worse off financially than you were a year ago?",
      // Wave 4 added a "not sure" option. Declaring wave 4 without it summed
      // to 97.1.
      keys: [
        "col__better_pred",
        "col__same_pred",
        "col__worse_pred",
        "col__notsure_pred",
      ],
      labels: {
        "col__better_pred":  "Better",
        "col__same_pred":    "About the same",
        "col__worse_pred":   "Worse",
        "col__notsure_pred": "Not sure",
      },
      polarity: {
        pos: "col__better_pred",
        neu: ["col__same_pred", "col__notsure_pred"],
        neg: "col__worse_pred",
      },
      schema: "tracking",
      // Waves 1 and 2 have the col__* columns but every row is null: the
      // question was not fielded until wave 3. Declaring them offered two wave
      // options that drew an empty map.
      waves: [3, 4],
    },

    pol_support_bbb: {
      type: "likert",
      label: "Income vs. prices",
      subgroup: "Personal Finances",
      fulltext: "Think about your household income versus the prices you pay for essentials. Would you say your paycheck is…",
      keys: [
        "income_v_prices__growing_faster_pred_n",
        "income_v_prices__keeping_up_pred_n",
        "income_v_prices__falling_behind_pred_n",
      ],
      labels: {
        "income_v_prices__growing_faster_pred_n": "Growing faster than prices",
        "income_v_prices__keeping_up_pred_n":     "Keeping up with prices",
        "income_v_prices__falling_behind_pred_n": "Falling behind prices",
      },
      polarity: {
        pos: "income_v_prices__growing_faster_pred_n",
        neu: "income_v_prices__keeping_up_pred_n",
        neg: "income_v_prices__falling_behind_pred_n",
      },
      schema: "tracking",
      waves: [3],
    },

    col_items: {
      type: "categorical",
      label: "Biggest monthly financial stress",
      subgroup: "Personal Finances",
      fulltext: "Which of the following monthly costs is currently causing the most financial stress for your household?",
      keys: [
        "col_items__groceries_pred_n",
        "col_items__housing_pred_n",
        "col_items__transport_pred_n",
        "col_items__debt_pred_n",
        "col_items__utilities_pred_n",
        "col_items__healthcare_pred_n",
        "col_items__other_pred_n",
        "col_items__none_pred_n",
      ],
      labels: {
        "col_items__groceries_pred_n": "Groceries and food",
        "col_items__housing_pred_n":   "Rent or mortgage payments",
        "col_items__transport_pred_n": "Gasoline and transportation",
        "col_items__debt_pred_n":      "Credit card or loan payments",
        "col_items__utilities_pred_n": "Utilities and energy bills",
        "col_items__healthcare_pred_n":"Healthcare costs",
        "col_items__other_pred_n":     "Other",
        "col_items__none_pred_n":      "None of the above",
      },
      schema: "tracking",
      // Wave 4 carries all eight col_items__* columns and sums to 100.
      waves: [3, 4],
    },

    col_responsible: {
      type: "categorical",
      label: "Cost of living responsibility",
      subgroup: "Personal Finances",
      fulltext: "Who or what do you believe is most responsible for the current cost of living in your area?",
      keys: [
        "col_responsible__president_pred_n",
        "col_responsible__fed_pred_n",
        "col_responsible__corporate_pred_n",
        "col_responsible__overseas_pred_n",
        "col_responsible__other_pred_n",
        "col_responsible__none_pred_n",
      ],
      labels: {
        "col_responsible__president_pred_n": "The current President and government spending",
        "col_responsible__fed_pred_n":       "Interest rate decisions by the Federal Reserve",
        "col_responsible__corporate_pred_n": "Corporate pricing and profits",
        "col_responsible__overseas_pred_n":  "Events overseas and foreign countries",
        "col_responsible__other_pred_n":     "Other",
        "col_responsible__none_pred_n":      "None of the above",
      },
      schema: "tracking",
      waves: [3],
    },

    // =========================================================================
    // EMPLOYMENT
    // =========================================================================

    job_loss_worry: {
      type: "likert",
      label: "Job loss worry",
      subgroup: "Employment",
      core: true,
      fulltext: "How worried are you about you or someone close to you losing a job in the next year?",
      keys: [
        "job_loss_worry__very_pred",
        "job_loss_worry__somewhat_pred",
        "job_loss_worry__nottoo_pred",
        "job_loss_worry__notatall_pred",
      ],
      labels: {
        "job_loss_worry__very_pred":     "Very worried",
        "job_loss_worry__somewhat_pred": "Somewhat worried",
        "job_loss_worry__nottoo_pred":   "Not too worried",
        "job_loss_worry__notatall_pred": "Not at all worried",
      },
      polarity: {
        neg: "job_loss_worry__very_pred",
        neu: "job_loss_worry__somewhat_pred",
        pos: "job_loss_worry__notatall_pred",
      },
      schema: "tracking",
      waves: [2, 3],
    },

    job_hunting: {
      type: "likert",
      label: "Job search intent",
      subgroup: "Employment",
      fulltext: "Do you plan to actively look for a new job in the next 3 months?",
      keys: [
        "job_hunting__yes_pred",
        "job_hunting__maybe_pred",
        "job_hunting__no_pred",
      ],
      labels: {
        "job_hunting__yes_pred":   "Yes, actively looking",
        "job_hunting__maybe_pred": "Maybe, depending on circumstances",
        "job_hunting__no_pred":    "No",
      },
      polarity: {
        pos: "job_hunting__yes_pred",
        neu: "job_hunting__maybe_pred",
        neg: "job_hunting__no_pred",
      },
      schema: "tracking",
      waves: [2, 3],
    },

    // =========================================================================
    // ATTRIBUTION
    // =========================================================================

    pol_blame_economy: {
      type: "categorical",
      label: "Blame for personal finances",
      subgroup: "Attribution",
      core: true,
      fulltext: "You said you are financially worse off now than you were a year ago. Who or what do you think deserves the most blame for this?",
      keys: [
        "blame_worse_off__cur_pres_pred",
        "blame_worse_off__prev_pres_pred",
        "blame_worse_off__neither_pred",
        "blame_worse_off__dk_pred",
      ],
      labels: {
        "blame_worse_off__cur_pres_pred":  "Current president",
        "blame_worse_off__prev_pres_pred": "Previous president",
        "blame_worse_off__neither_pred":   "Neither / Some other reason",
        "blame_worse_off__dk_pred":        "Don't know",
      },
      colors: {
        "blame_worse_off__cur_pres_pred":  "#D52B1E",
        "blame_worse_off__prev_pres_pred": "#0057B7",
        "blame_worse_off__neither_pred":   "#888888",
        "blame_worse_off__dk_pred":        "#CCCCCC",
      },
      schema: "tracking",
      // Wave 1 has the blame_worse_off__* columns but every row is null: the
      // question was not fielded until wave 2. Wave 4 dropped it again.
      waves: [2, 3],
    },

    credit_better_off: {
      type: "categorical",
      label: "Credit for personal finances",
      subgroup: "Attribution",
      fulltext: "You said you are financially better off now than you were a year ago. Who or what do you think deserves the most credit for this?",
      keys: [
        "credit_better_off__cur_pres_pred",
        "credit_better_off__prev_pres_pred",
        "credit_better_off__neither_pred",
        "credit_better_off__dk_pred",
      ],
      labels: {
        "credit_better_off__cur_pres_pred":  "Current president",
        "credit_better_off__prev_pres_pred": "Previous president",
        "credit_better_off__neither_pred":   "Neither / Some other reason",
        "credit_better_off__dk_pred":        "Don't know",
      },
      colors: {
        "credit_better_off__cur_pres_pred":  "#D52B1E",
        "credit_better_off__prev_pres_pred": "#0057B7",
        "credit_better_off__neither_pred":   "#888888",
        "credit_better_off__dk_pred":        "#CCCCCC",
      },
      schema: "tracking",
      waves: [2, 3],
    },

  };

  return { groupsById };
}

const DISTINCT = ["#4e79a7","#f28e2b","#e15759","#76b7b2","#59a14f","#edc948","#b07aa1","#ff9da7","#9c755f","#bab0ab"];

export function buildLabelColorMaps(groupsById) {
  const labels = {};
  const colors = {};
  let colorIdx = 0;

  for (const g of Object.values(groupsById)) {
    for (const k of g.keys) {
      labels[k] = g.full_labels?.[k] || g.labels?.[k] || k;

      if (!colors[k]) {
        if (g.colors?.[k]) {
          colors[k] = g.colors[k];
        } else if (g.type === "likert") {
          colors[k] = baseColorForLikertKey(g, k);
        } else {
          colors[k] = DISTINCT[colorIdx % DISTINCT.length];
          colorIdx += 1;
        }
      }
    }
  }

  return { labels, colors };
}

export function aggregateWeighted(rows, keys, { rowKey, weight = "total", predicate = null } = {}) {
  const acc = Object.create(null);
  const zero = Object.fromEntries(keys.map(k => [k, 0]));

  for (const r of rows || []) {
    if (predicate && !predicate(r)) continue;
    const d = r[rowKey];
    if (!d) continue;
    const w = Number(r[weight] ?? 0);
    if (!acc[d]) acc[d] = { w: 0, sums: { ...zero } };
    acc[d].w += w;
    for (const k of keys) acc[d].sums[k] += Number(r[k] ?? 0) * w;
  }

  const out = {};
  for (const [d, { w, sums }] of Object.entries(acc)) {
    const values = {};
    for (const k of keys) values[k] = w > 0 ? (sums[k] / w) * 100 : 0;
    out[d] = { totalWeight: w, values };
  }
  return out;
}

export function buildDemoPredicate({
  gender    = null,
  age       = null,
  ethnicity = null,
  party     = null,
  education = null,
} = {}) {
  return (r) => {
    if (gender    != null && Number(r.gender)    !== Number(gender))    return false;
    if (age       != null && Number(r.age)        !== Number(age))       return false;
    if (ethnicity != null && Number(r.ethnicity)  !== Number(ethnicity)) return false;
    if (party     != null && Number(r.party_reg)  !== Number(party))     return false;
    if (education != null && Number(r.education)  !== Number(education)) return false;
    return true;
  };
}

export const GENDER = [
  { value: null, label: "All" },
  { value: 1,    label: "Men" },
  { value: 2,    label: "Women" },
];

export const AGE = [
  { value: null, label: "All" },
  { value: 1,    label: "18–34" },
  { value: 2,    label: "35–64" },
  { value: 3,    label: "65+" },
];

export const ETHNICITY = [
  { value: null, label: "All" },
  { value: 1,    label: "White" },
  { value: 2,    label: "Black" },
  { value: 3,    label: "Hispanic" },
  { value: 4,    label: "Asian" },
  { value: 5,    label: "Other" },
];

export const PARTY = [
  { value: null, label: "All" },
  { value: 1,    label: "Dem" },
  { value: 2,    label: "Rep" },
  { value: 3,    label: "Ind" },
  { value: 4,    label: "None" },
];

export const EDU = [
  { value: null, label: "All" },
  { value: 1,    label: "No degree" },
  { value: 2,    label: "Degree" },
];

export function baseColorForLikertKey(group, key) {
  const pol = group?.polarity || {};

  const tag = Object.entries(pol).find(([_, polKey]) => {
    if (Array.isArray(polKey)) return polKey.includes(key);
    return polKey === key;
  })?.[0];

  if (tag === "pos") return "#2E7D32";
  if (tag === "neg") return "#C62828";
  if (tag === "neu") return "#bab0ab";
  return "#6e6e6e";
}

export function getDemoPolConfig() {
  const { groupsById } = getPolConfig();
  const demoKeys = [
    "econ_approve",
    "col_items",
    "pol_top_issues",
    "pol_econ_direction",
    "immig_attitudes",
    //"pol_tariffs_effect",
    "pol_midterm_vote_choice",
  ];
  return {
    groupsById: Object.fromEntries(
      demoKeys.map(k => [k, { ...groupsById[k], subgroup: null }])
    ),
  };
}

export default {
  tab: "pol",
  label: "Tracking",
  get metrics() { return getPolConfig().groupsById; },
};
